// Cricket Academies and Pathway Information

export const indianCricketPathway = {
  title: "Indian Cricket Pathway",
  description: "The structured pathway from grassroots to international cricket in India",
  levels: [
    {
      id: 1,
      name: "Under-14 / School Cricket",
      age: "10-14 years",
      description: "Entry point through school cricket and district academies. Focus on fundamentals and love for the game.",
      tournaments: ["Harris Shield (Mumbai)", "Cooch Behar Trophy", "State U-14 Championships"],
      selectionPath: "District trials → State academy selection"
    },
    {
      id: 2,
      name: "Under-16 Cricket",
      age: "14-16 years",
      description: "State-level competition with BCCI recognized tournaments. Introduction to competitive cricket.",
      tournaments: ["Vijay Merchant Trophy", "State U-16 Championships"],
      selectionPath: "State U-16 squad → Zonal camps → National camp"
    },
    {
      id: 3,
      name: "Under-19 Cricket",
      age: "16-19 years",
      description: "The most crucial age group. Performances here directly impact first-class selection.",
      tournaments: ["Cooch Behar Trophy", "Vinoo Mankad Trophy (One-Day)", "U-19 World Cup"],
      selectionPath: "State U-19 → Zonal U-19 → India U-19 squad"
    },
    {
      id: 4,
      name: "Under-23 / CK Nayudu Trophy",
      age: "19-23 years",
      description: "Bridge between age-group and senior cricket. Last step before Ranji Trophy.",
      tournaments: ["CK Nayudu Trophy", "State Senior selection trials"],
      selectionPath: "Consistent performance → Ranji Trophy squad"
    },
    {
      id: 5,
      name: "Ranji Trophy (First-Class)",
      age: "Open age",
      description: "India's premier first-class competition. The primary pathway to Test cricket.",
      tournaments: ["Ranji Trophy", "Irani Cup", "Duleep Trophy"],
      selectionPath: "500+ runs / 20+ wickets per season → India A consideration"
    },
    {
      id: 6,
      name: "Vijay Hazare Trophy (List A)",
      age: "Open age",
      description: "50-over domestic competition. Pathway to ODI cricket.",
      tournaments: ["Vijay Hazare Trophy", "Deodhar Trophy"],
      selectionPath: "Consistent run-scoring → India A ODI squad"
    },
    {
      id: 7,
      name: "Syed Mushtaq Ali Trophy (T20)",
      age: "Open age",
      description: "Domestic T20 competition. Gateway to IPL and T20I cricket.",
      tournaments: ["Syed Mushtaq Ali Trophy"],
      selectionPath: "Good strike rate & impact → IPL auction attention"
    },
    {
      id: 8,
      name: "Indian Premier League (IPL)",
      age: "Open age",
      description: "World's biggest T20 league. Massive exposure and learning opportunity.",
      tournaments: ["IPL"],
      selectionPath: "Strong domestic T20 numbers → Auction selection → Performance → India T20I"
    },
    {
      id: 9,
      name: "India A",
      age: "Open age",
      description: "Shadow national team. Final proving ground before international debut.",
      tournaments: ["India A Tours", "Emerging Teams Tournament"],
      selectionPath: "Consistent India A performance → Senior India call-up"
    },
    {
      id: 10,
      name: "Indian National Team",
      age: "Open age",
      description: "The ultimate goal. Representing India in international cricket.",
      tournaments: ["Test Matches", "ODI World Cup", "T20 World Cup", "ICC Events"],
      selectionPath: "Maintain performance → Retain spot → Legacy"
    }
  ]
};

export const topCricketAcademies = [
  {
    id: 1,
    name: "National Cricket Academy (NCA)",
    location: "Bangalore, Karnataka",
    type: "BCCI Official",
    description: "India's premier cricket training facility. Handles injury rehabilitation and skill development for national team prospects.",
    facilities: ["6 turf wickets", "Indoor facilities", "Gym", "Pool", "Sports science lab"],
    notable: "Official BCCI academy - not open for public enrollment",
    website: "https://www.bcci.tv"
  },
  {
    id: 2,
    name: "MRF Pace Foundation",
    location: "Chennai, Tamil Nadu",
    type: "Private Elite",
    description: "World-renowned pace bowling academy that has produced legends like Glenn McGrath mentored by Dennis Lillee.",
    facilities: ["Specialized pace bowling tracks", "Video analysis", "Biomechanics lab"],
    notable: "Alumni: Zaheer Khan, Irfan Pathan, Jasprit Bumrah",
    website: "https://mrfpace.com"
  },
  {
    id: 3,
    name: "Karnataka Institute of Cricket (KIOC)",
    location: "Bangalore, Karnataka",
    type: "Private",
    description: "Founded by Javagal Srinath and Roger Binny. Known for producing quality fast bowlers.",
    facilities: ["Multiple grounds", "Indoor nets", "Hostel"],
    notable: "Strong focus on pace bowling development",
    website: "https://kioc.in"
  },
  {
    id: 4,
    name: "VB Cricket Academy",
    location: "Chennai, Tamil Nadu",
    type: "Private",
    description: "Run by VB Chandrasekhar. Known for producing Tamil Nadu cricketers.",
    facilities: ["Turf wickets", "Bowling machines", "Video analysis"],
    notable: "Focus on technical development for young cricketers",
    website: ""
  },
  {
    id: 5,
    name: "Vengsarkar Cricket Academy",
    location: "Mumbai, Maharashtra",
    type: "Private Elite",
    description: "Founded by Dilip Vengsarkar. Has produced numerous India internationals.",
    facilities: ["Multiple grounds", "Indoor facilities", "Expert coaching"],
    notable: "Alumni: Rohit Sharma, Ajinkya Rahane, Shreyas Iyer",
    website: ""
  },
  {
    id: 6,
    name: "Dravid Cricket Academy",
    location: "Bangalore, Karnataka",
    type: "Private",
    description: "Associated with Rahul Dravid's coaching philosophy. Focus on technique and mental strength.",
    facilities: ["Modern training facilities", "Video analysis"],
    notable: "Emphasis on proper technique from young age",
    website: ""
  },
  {
    id: 7,
    name: "MS Dhoni Cricket Academy",
    location: "Multiple locations",
    type: "Franchise",
    description: "Academy chain focused on grassroots development inspired by MS Dhoni's approach.",
    facilities: ["Standard coaching facilities", "Multiple centers across India"],
    notable: "Accessible grassroots program",
    website: ""
  },
  {
    id: 8,
    name: "Madan Lal Cricket Academy",
    location: "Delhi",
    type: "Private",
    description: "Run by 1983 World Cup hero Madan Lal. Strong focus on all-round development.",
    facilities: ["Indoor and outdoor nets", "Gym facilities"],
    notable: "Personal attention from experienced coaches",
    website: ""
  }
];

export const internationalPathways = [
  {
    country: "Australia",
    pathway: [
      "Club Cricket → Premier Cricket",
      "State U-17 → State U-19",
      "Sheffield Shield (First-Class)",
      "One-Day Cup / Big Bash League",
      "Australia A → National Team"
    ],
    keyTournament: "Sheffield Shield",
    uniqueFeature: "Strong club cricket culture, pathway clearly defined"
  },
  {
    country: "England",
    pathway: [
      "Club Cricket → County Age Groups",
      "County Academy → County 2nd XI",
      "County Championship (First-Class)",
      "Royal London Cup / The Hundred",
      "England Lions → National Team"
    ],
    keyTournament: "County Championship",
    uniqueFeature: "Professional county system from age 16"
  },
  {
    country: "Pakistan",
    pathway: [
      "School/Club Cricket → Regional U-19",
      "Pakistan U-19 → First-Class",
      "Quaid-e-Azam Trophy",
      "Pakistan Super League",
      "Pakistan Shaheens → National Team"
    ],
    keyTournament: "Quaid-e-Azam Trophy",
    uniqueFeature: "PCB regional academy system"
  },
  {
    country: "South Africa",
    pathway: [
      "School Cricket → Provincial Academy",
      "Provincial U-19 → Amateur Provincial",
      "CSA 4-Day Series",
      "CSA T20 Challenge / SA20",
      "South Africa A → National Team"
    ],
    keyTournament: "CSA 4-Day Series",
    uniqueFeature: "Strong school cricket tradition"
  },
  {
    country: "West Indies",
    pathway: [
      "Island Club Cricket → Island U-19",
      "Regional U-19 → Regional 4-Day",
      "West Indies Championship",
      "Caribbean Premier League",
      "West Indies A → National Team"
    ],
    keyTournament: "West Indies Championship",
    uniqueFeature: "Multi-nation selection complexity"
  }
];

export const domesticTournaments = {
  india: [
    {
      name: "Ranji Trophy",
      format: "First-Class (4-day)",
      teams: 38,
      description: "India's premier red-ball competition since 1934. Named after Ranjitsinhji.",
      importance: "Primary pathway to Test cricket"
    },
    {
      name: "Vijay Hazare Trophy",
      format: "List A (50-over)",
      teams: 38,
      description: "National 50-over championship named after the legendary batsman.",
      importance: "Pathway to ODI cricket"
    },
    {
      name: "Syed Mushtaq Ali Trophy",
      format: "T20",
      teams: 38,
      description: "National T20 championship, often used for IPL scouting.",
      importance: "Gateway to IPL and T20I cricket"
    },
    {
      name: "Duleep Trophy",
      format: "First-Class (4-day)",
      teams: 6,
      description: "Zonal tournament featuring India's best domestic players.",
      importance: "Selection trial for India A and senior team"
    },
    {
      name: "Deodhar Trophy",
      format: "List A (50-over)",
      teams: 3,
      description: "Zonal 50-over competition at elite level.",
      importance: "Final audition for national ODI team"
    },
    {
      name: "Irani Cup",
      format: "First-Class (4-day)",
      teams: 2,
      description: "Ranji Trophy champions vs Rest of India.",
      importance: "Prestigious end-of-season fixture"
    }
  ]
};
